import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
  Button,
  Stack,
  Divider,
  useMediaQuery,
  Popper,
  Paper,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Chip,
  InputBase,
} from "@mui/material";
import {
  Send as SendIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachFile as AttachFileIcon,
  Chat as ChatIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import moment from "moment";
import {
  useGetEntityNotificationsQuery,
  useCreateNotificationMutation,
  useUploadNotificationAttachmentsMutation,
  useLazySearchMentionContactsQuery,
  useUpdateMarkAsReadAllByEntityMutation,
} from "api/api-notifications";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import type {
  INotification,
  INotificationAttachment,
  IMentionContact,
} from "api/api-notifications.d";

interface LeadCommentsModalProps {
  open: boolean;
  onClose: () => void;
  leadId: string;
  leadName: string;
  accountId?: string; // Account ID for sending notifications
  /** Optional: override default header title ("Comments"). */
  title?: string;
  /**
   * Optional: when true, restricts @-mentions to internal Risk team only.
   * Implemented client-side by filtering mention search results to Risk departments.
   */
  restrictMentionsToRisk?: boolean;
  /** When true, enables @-mentions (default: only for MAP entity ids). */
  enableMentions?: boolean;
  presentation?: "chat" | "risk-workspace";
}

interface CommentItemProps {
  comment: INotification;
  currentUser?: any;
  presentation?: "chat" | "risk-workspace";
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  currentUser,
  presentation = "chat",
}) => {
  const theme = useTheme();

  // Determine if the message is from the current user (for alignment)
  // Try multiple ways to match the current user
  const currentUserFullName = currentUser
    ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
    : "";
  const currentUserEmail = currentUser?.email || "";

  const isCurrentUserMessage =
    currentUser &&
    comment.contact_name &&
    (comment.contact_name.trim() === currentUserFullName ||
      comment.contact_name.trim() === `${currentUser.first_name}` ||
      comment.contact_name.trim() === `${currentUser.last_name}` ||
      (currentUserEmail &&
        comment.contact_name
          .toLowerCase()
          .includes(currentUserEmail.toLowerCase().split("@")[0])) ||
      // Additional matching for client side - check if comment contact name contains parts of current user name
      comment.contact_name
        .toLowerCase()
        .includes(currentUser.first_name?.toLowerCase() || "") ||
      comment.contact_name
        .toLowerCase()
        .includes(currentUser.last_name?.toLowerCase() || "") ||
      // Check for "User" suffix (like "Atext Chemical User")
      (comment.contact_name.toLowerCase().includes("user") &&
        !currentUser.is_internal));

  // Determine if the sender is an internal user (for color)
  // Check if the sender's name contains internal indicators or if current user is internal and it's their message
  const isInternalSender =
    (currentUser?.is_internal && isCurrentUserMessage) ||
    (comment.contact_name &&
      comment.contact_name.toLowerCase().includes("incomlend"));
  // comment.contact_name.toLowerCase().includes('admin') ||
  // comment.contact_name.toLowerCase().includes('team') ||
  // comment.contact_name.toLowerCase().includes('manager') ||
  // comment.contact_name.toLowerCase().includes('support')

  // Determine message alignment and colors
  const getMessageStyle = () => {
    // Current user's messages go to the right, others to the left
    const isRightAligned = isCurrentUserMessage;

    // Color based on sender type: internal = green, external = blue/gray
    if (isRightAligned) {
      // Current user's messages on the right
      if (isInternalSender) {
        // Internal user (green)
        return {
          align: "flex-end" as const,
          flexDirection: "row-reverse" as const,
          borderRadius: "18px 18px 4px 18px",
          background: "linear-gradient(135deg, #4CAF50 0%, #45A049 100%)", // Green for internal team
          textColor: "#FFFFFF",
          avatarBg: "#4CAF50",
          tailPosition: "right" as const,
        };
      } else {
        // External user (blue)
        return {
          align: "flex-end" as const,
          flexDirection: "row-reverse" as const,
          borderRadius: "18px 18px 4px 18px",
          background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)", // Blue for external users
          textColor: "#FFFFFF",
          avatarBg: "#2196F3",
          tailPosition: "right" as const,
        };
      }
    } else {
      // Other users' messages on the left
      if (isInternalSender) {
        // Internal user (green)
        return {
          align: "flex-start" as const,
          flexDirection: "row" as const,
          borderRadius: "18px 18px 18px 4px",
          background: "linear-gradient(135deg, #4CAF50 0%, #45A049 100%)", // Green for internal team
          textColor: "#FFFFFF",
          avatarBg: "#4CAF50",
          tailPosition: "left" as const,
        };
      } else {
        // External user (gray)
        return {
          align: "flex-start" as const,
          flexDirection: "row" as const,
          borderRadius: "18px 18px 18px 4px",
          background: "linear-gradient(135deg, #E4E6EA 0%, #D1D5DB 100%)", // Light gray for external clients
          textColor: "#1C1E21",
          avatarBg: "#9CA3AF",
          tailPosition: "left" as const,
        };
      }
    }
  };

  const messageStyle = getMessageStyle();

  const attachments = (comment as any).attachments || [];
  const messageText = (comment.message || comment.content || "").toString();

  if (presentation === "risk-workspace") {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 1,
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Stack spacing={1.25}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  fontSize: "0.75rem",
                  bgcolor: isInternalSender
                    ? theme.palette.success.main
                    : theme.palette.primary.main,
                }}
              >
                {isInternalSender ? (
                  <BusinessIcon sx={{ fontSize: 16 }} />
                ) : (
                  <PersonIcon sx={{ fontSize: 16 }} />
                )}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  {comment.contact_name || comment.title || "Risk user"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {moment(comment.send_at).format("DD MMM YYYY, HH:mm")}
                </Typography>
              </Box>
            </Stack>
            {attachments.length > 0 && (
              <Chip
                size="small"
                icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                label={`${attachments.length} attachment${attachments.length === 1 ? "" : "s"}`}
                variant="outlined"
              />
            )}
          </Stack>

          {messageText && messageText !== "📎 Attachment" && (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                color: "text.primary",
                lineHeight: 1.6,
              }}
            >
              {messageText}
            </Typography>
          )}

          {attachments.length > 0 && (
            <Stack spacing={0.75}>
              {attachments.map((att: INotificationAttachment) => (
                <Box
                  key={att.id}
                  component="a"
                  href={att.url || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.25,
                    py: 0.85,
                    borderRadius: 1,
                    color: "text.primary",
                    bgcolor: theme.palette.action.hover,
                    border: `1px solid ${theme.palette.divider}`,
                    textDecoration: "none",
                    cursor: att.url ? "pointer" : "default",
                    "&:hover": att.url
                      ? {
                          borderColor: theme.palette.primary.main,
                          bgcolor: theme.palette.action.selected,
                        }
                      : {},
                  }}
                >
                  <AttachFileIcon
                    sx={{ fontSize: 18, color: theme.palette.primary.main }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {att.name}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>
    );
  }

  return (
    <Box
      className="comment-item"
      sx={{
        display: "flex",
        justifyContent: messageStyle.align,
        mb: { xs: 1.5, sm: 2 },
        px: { xs: 1.5, sm: 2 },
        py: 0.5,
        animation: "fadeInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        "@keyframes fadeInUp": {
          "0%": {
            opacity: 0,
            transform: "translateY(15px) scale(0.95)",
          },
          "100%": {
            opacity: 1,
            transform: "translateY(0) scale(1)",
          },
        },
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: "85%", sm: "80%" },
          display: "flex",
          flexDirection: messageStyle.flexDirection,
          alignItems: "flex-end",
          gap: { xs: 1, sm: 1.5 },
        }}
      >
        <Avatar
          sx={{
            background: messageStyle.avatarBg,
            width: { xs: 28, sm: 32 },
            height: { xs: 28, sm: 32 },
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
            fontWeight: "bold",
            boxShadow: theme.shadows[2],
            border: "2px solid rgba(255,255,255,0.2)",
            flexShrink: 0,
          }}
        >
          {isInternalSender ? (
            <BusinessIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
          ) : (
            <PersonIcon
              sx={{
                fontSize: 16,
                color:
                  messageStyle.textColor === "#FFFFFF" ? "#FFFFFF" : "#6B7280",
              }}
            />
          )}
        </Avatar>

        <Box
          sx={{
            background: messageStyle.background,
            color: messageStyle.textColor,
            borderRadius: messageStyle.borderRadius,
            p: { xs: 1.25, sm: 1.5 },
            boxShadow:
              "0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)",
            minWidth: 60,
            maxWidth: "100%",
            position: "relative",
            "&::before":
              messageStyle.tailPosition === "right"
                ? {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    right: -6,
                    width: 0,
                    height: 0,
                    borderLeft: "6px solid transparent",
                    borderTop: `6px solid ${messageStyle.avatarBg}`,
                  }
                : {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: -6,
                    width: 0,
                    height: 0,
                    borderRight: "6px solid transparent",
                    borderTop: `6px solid ${messageStyle.avatarBg}`,
                  },
          }}
        >
          {/* Show message text if present, with formatted tags for departments & users */}
          {(comment.message || comment.content) &&
            comment.message !== "📎 Attachment" &&
            comment.content !== "📎 Attachment" && (
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.5,
                  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                  fontWeight: 500,
                  color: `${messageStyle.textColor} !important`,
                  textShadow:
                    messageStyle.textColor === "#FFFFFF"
                      ? "0 1px 2px rgba(0,0,0,0.15)"
                      : "none",
                  wordBreak: "break-word",
                  display: "inline",
                }}
              >
                {(() => {
                  const raw = (
                    comment.message ||
                    comment.content ||
                    ""
                  ).toString();
                  const parts: React.ReactNode[] = [];
                  const tagRegex = /\[\[(DEPT|USER):(.+?)\]\]/g;
                  let lastIndex = 0;

                  // Iterate over tag matches without assigning inside the while condition
                  // to satisfy eslint no-cond-assign while preserving behaviour
                  // (global regex state is advanced via exec on each loop).
                  // eslint-disable-next-line no-constant-condition
                  while (true) {
                    const match = tagRegex.exec(raw);
                    if (!match) break;

                    const [full, kind, name] = match;
                    if (match.index > lastIndex) {
                      parts.push(
                        <span key={`text-${lastIndex}`}>
                          {raw.slice(lastIndex, match.index)}
                        </span>,
                      );
                    }

                    const displayName = name.trim();
                    const isDept = kind === "DEPT";

                    parts.push(
                      <Box
                        key={`tag-${match.index}`}
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          px: 0.8,
                          py: 0.1,
                          mr: 0.5,
                          mb: 0.25,
                          borderRadius: 999,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          letterSpacing: 0.3,
                          textTransform: "uppercase",
                          backgroundColor: isDept
                            ? "rgba(255,255,255,0.18)"
                            : "rgba(0,0,0,0.12)",
                          border: `1px solid ${
                            isDept
                              ? "rgba(255,255,255,0.6)"
                              : "rgba(0,0,0,0.18)"
                          }`,
                          color:
                            messageStyle.textColor === "#FFFFFF"
                              ? "#FFFFFF"
                              : isDept
                                ? "#0B5ED7"
                                : "#111827",
                          boxShadow: isDept
                            ? "0 0 0 1px rgba(255,255,255,0.25)"
                            : "none",
                        }}
                      >
                        <span
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: "999px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: 4,
                            fontSize: 9,
                            fontWeight: 800,
                            backgroundColor: isDept
                              ? "#4F46E5"
                              : "rgba(255,255,255,0.9)",
                            color: isDept ? "#FFFFFF" : "#111827",
                          }}
                        >
                          {isDept ? "D" : "@"}
                        </span>
                        {displayName}
                      </Box>,
                    );

                    lastIndex = match.index + full.length;
                  }

                  if (lastIndex < raw.length) {
                    parts.push(
                      <span key="text-end">{raw.slice(lastIndex)}</span>,
                    );
                  }

                  return parts;
                })()}
              </Typography>
            )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <Box
              mt={
                (comment.message && comment.message !== "📎 Attachment") ||
                (comment.content && comment.content !== "📎 Attachment")
                  ? 1.5
                  : 0
              }
              display="flex"
              flexDirection="column"
              gap={0.75}
            >
              {attachments.map((att: INotificationAttachment) => (
                <Box
                  key={att.id}
                  component="a"
                  href={att.url || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1.5,
                    bgcolor:
                      messageStyle.textColor === "#FFFFFF"
                        ? "rgba(255,255,255,0.2)"
                        : "rgba(0,0,0,0.06)",
                    textDecoration: "none",
                    cursor: att.url ? "pointer" : "default",
                    border: `1px solid ${
                      messageStyle.textColor === "#FFFFFF"
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.1)"
                    }`,
                    transition: "all 0.2s ease",
                    "&:hover": att.url
                      ? {
                          bgcolor:
                            messageStyle.textColor === "#FFFFFF"
                              ? "rgba(255,255,255,0.3)"
                              : "rgba(0,0,0,0.1)",
                          transform: "translateY(-1px)",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                        }
                      : {},
                  }}
                  title={`${att.name}${
                    att.mime_type ? `\n${att.mime_type}` : ""
                  }${
                    att.size
                      ? `\n${(att.size / 1024 / 1024).toFixed(2)} MB`
                      : ""
                  }`}
                >
                  <AttachFileIcon
                    sx={{
                      fontSize: { xs: 14, sm: 16 },
                      mr: 0.75,
                      color:
                        messageStyle.textColor === "#FFFFFF"
                          ? "#FFFFFF"
                          : "#4B5563",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: `${messageStyle.textColor} !important`,
                      textDecoration: att.url ? "underline" : "none",
                      maxWidth: { xs: 140, sm: 180 },
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      fontWeight: 600,
                    }}
                  >
                    {att.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Typography
            variant="caption"
            sx={{
              opacity: messageStyle.textColor === "#FFFFFF" ? 0.85 : 0.7,
              display: "block",
              mt:
                comment.message || comment.content || attachments.length > 0
                  ? 0.75
                  : 0,
              fontSize: { xs: "0.65rem", sm: "0.7rem" },
              textAlign: messageStyle.tailPosition,
              color: `${messageStyle.textColor} !important`,
              textShadow:
                messageStyle.textColor === "#FFFFFF"
                  ? "0 1px 2px rgba(0,0,0,0.15)"
                  : "none",
              fontWeight: 500,
            }}
          >
            {comment.contact_name || comment.title || "Unknown User"} •{" "}
            {moment(comment.send_at)
              .utcOffset("+05:30")
              .format("DD MMM YYYY, HH:mm [IST]")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const LeadCommentsModal: React.FC<LeadCommentsModalProps> = ({
  open,
  onClose,
  leadName: _leadName,
  accountId,
  title = "Comments",
  restrictMentionsToRisk = false,
  enableMentions: enableMentionsProp,
  presentation = "chat",
}) => {
  const theme = useTheme();
  const isRiskWorkspace = presentation === "risk-workspace";
  const [newComment, setNewComment] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  /** JIRA-style: selected mentions as chips (id + display name). Only these IDs are sent to API. */
  const [mentionChips, setMentionChips] = useState<
    { id: string; name: string; type: "contact" | "department" }[]
  >([]);
  const [
    mentionDropdownClosedUntilChange,
    setMentionDropdownClosedUntilChange,
  ] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const inputAnchorRef = useRef<HTMLDivElement>(null);

  const enableMentions =
    enableMentionsProp ??
    Boolean(accountId && /^MAP-|^P-MAP-/i.test(String(accountId)));

  const mentionQuery = useMemo(() => {
    if (!enableMentions) return "";
    const lastAt = newComment.lastIndexOf("@");
    if (lastAt === -1) return "";
    const after = newComment.slice(lastAt + 1);
    const space = after.search(/\s/);
    return space === -1 ? after : after.slice(0, space);
  }, [newComment, enableMentions]);

  const [debouncedMentionQuery, setDebouncedMentionQuery] = useState("");
  useEffect(() => {
    if (!mentionQuery) {
      setDebouncedMentionQuery("");
      return;
    }
    const timer = window.setTimeout(() => {
      setDebouncedMentionQuery(mentionQuery);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [mentionQuery]);

  const [
    searchMentionContacts,
    { data: mentionContacts = [], isFetching: isMentionSearching },
  ] = useLazySearchMentionContactsQuery();
  useEffect(() => {
    if (!enableMentions || debouncedMentionQuery.length < 2) return;
    searchMentionContacts(debouncedMentionQuery);
  }, [enableMentions, debouncedMentionQuery, searchMentionContacts]);

  const filteredMentionContacts = useMemo(() => {
    if (!restrictMentionsToRisk) return mentionContacts;
    return mentionContacts.filter((c: IMentionContact) => {
      const displayName =
        c.name || [c.first_name, c.last_name].filter(Boolean).join(" ") || "";
      const name = displayName.toLowerCase();
      // Keep only department-level options that clearly represent Risk
      return Boolean(c.is_department) && name.includes("risk");
    });
  }, [mentionContacts, restrictMentionsToRisk]);

  const [markAllByEntity] = useUpdateMarkAsReadAllByEntityMutation();

  const showMentionPopper =
    enableMentions &&
    !mentionDropdownClosedUntilChange &&
    mentionQuery.length >= 2 &&
    (filteredMentionContacts.length > 0 || isMentionSearching);

  // Get current user for message alignment and styling
  const { data: currentUser } = useGetCurrentUserQuery();

  // Check if profile sidebar drawer is open by checking DOM
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const drawerWidth = 260;
  const matchesMd = useMediaQuery(theme.breakpoints.up("md"));

  useEffect(() => {
    // Check if the profile drawer is visible in the DOM
    const checkDrawer = () => {
      // Try multiple methods to find the drawer
      const drawerElement = document.querySelector(
        '[data-testid="profile-sidebar-drawer"]',
      );

      if (!drawerElement) {
        setProfileDrawerOpen(false);
        return;
      }

      // Look for the actual drawer paper element
      const drawerPaper = drawerElement.querySelector(".MuiDrawer-paper");

      if (!drawerPaper) {
        setProfileDrawerOpen(false);
        return;
      }

      // Check if drawer is actually visible (not hidden with transform)
      const style = window.getComputedStyle(drawerPaper);
      const transform = style.transform;
      const display = style.display;
      const visibility = style.visibility;

      // Drawer is open if it's visible and not transformed off-screen
      const isOpen =
        display !== "none" &&
        visibility !== "hidden" &&
        (transform === "none" ||
          transform.includes("translateX(0px)") ||
          transform.includes("translate(0px)") ||
          transform.includes("translate3d(0px") ||
          transform.includes("translateX(0)") ||
          transform.includes("translate(0)") ||
          transform.includes("translate3d(0"));

      setProfileDrawerOpen(Boolean(isOpen) && matchesMd);
    };

    // Check immediately when modal opens
    if (open) {
      checkDrawer();
    }

    // Set up interval to continuously check
    const interval = setInterval(checkDrawer, 200); // Check every 200ms for better responsiveness

    return () => clearInterval(interval);
  }, [open, matchesMd]);

  // Use Notification API hooks
  const {
    data: entityComments = [],
    isLoading,
    refetch,
  } = useGetEntityNotificationsQuery(accountId || "", {
    skip: !open || !accountId,
  });
  // Thread rows have no category; bell copies (mentions, WD alerts) must not appear as messages.
  const comments = useMemo(
    () => entityComments.filter((c) => !c.category),
    [entityComments],
  );
  const sortedComments = useMemo(
    () =>
      comments
        .slice()
        .sort(
          (a, b) =>
            new Date(a.send_at).getTime() - new Date(b.send_at).getTime(),
        ),
    [comments],
  );

  const allAttachments = useMemo(
    () =>
      sortedComments.flatMap((comment) =>
        ((comment as any).attachments || []).map(
          (attachment: INotificationAttachment) => ({
            ...attachment,
            commentId: comment.id,
            contactName: comment.contact_name || comment.title || "Risk user",
            sendAt: comment.send_at,
          }),
        ),
      ),
    [sortedComments],
  );
  const [createNotification, { isLoading: isCreating }] =
    useCreateNotificationMutation();
  const [uploadAttachments, { isLoading: isUploading }] =
    useUploadNotificationAttachmentsMutation();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (open && accountId) {
      // Query runs when open/accountId are set (skip clears); do not refetch before it starts.
      setMentionDropdownClosedUntilChange(false);
      if (enableMentions) {
        setMentionChips([]);
        markAllByEntity(accountId).catch(() => {});
      }
    }
  }, [open, accountId, enableMentions, markAllByEntity]);

  useEffect(() => {
    if (!isRiskWorkspace) {
      scrollToBottom();
    }
  }, [comments, isRiskWorkspace]);

  const handleSendComment = async () => {
    // Allow sending if there's either a message or files
    if ((!newComment.trim() && selectedFiles.length === 0) || !accountId)
      return;

    try {
      let attachments: INotificationAttachment[] | undefined;

      // Upload attachments if any have been selected
      if (selectedFiles.length) {
        // Basic client-side validation
        const maxSizeBytes = 10 * 1024 * 1024; // 10 MB per file
        const allowedMimeTypes = [
          "application/pdf",
          "image/png",
          "image/jpeg",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ];

        for (const file of selectedFiles) {
          if (file.size > maxSizeBytes) {
            toast.error(`File ${file.name} exceeds 10MB limit`);
            return;
          }
          if (file.type && !allowedMimeTypes.includes(file.type)) {
            toast.error(`File type not allowed: ${file.name}`);
            return;
          }
        }

        attachments = await uploadAttachments({
          files: selectedFiles,
        }).unwrap();
      }

      const tagTokens =
        enableMentions && mentionChips.length
          ? mentionChips.map((c) =>
              c.type === "department"
                ? `[[DEPT:${c.name}]]`
                : `[[USER:${c.name}]]`,
            )
          : [];

      const messageParts = [...tagTokens, newComment.trim()].filter(Boolean);
      const message =
        messageParts.join(" ") ||
        (selectedFiles.length > 0 ? "📎 Attachment" : "");

      const mentionedContactIds = enableMentions
        ? mentionChips.filter((c) => c.type === "contact").map((c) => c.id)
        : [];
      const mentionedDepartmentIds = enableMentions
        ? mentionChips.filter((c) => c.type === "department").map((c) => c.id)
        : [];

      await createNotification({
        entity_id: String(accountId),
        message,
        ...(mentionedContactIds.length > 0 && {
          mentioned_contact_ids: mentionedContactIds,
        }),
        ...(mentionedDepartmentIds.length > 0 && {
          mentioned_department_ids: mentionedDepartmentIds,
        }),
        attachments,
      }).unwrap();

      setNewComment("");
      setSelectedFiles([]);
      setMentionChips([]);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      toast.success(
        selectedFiles.length > 0 && !newComment.trim()
          ? "File sent successfully"
          : "Message sent successfully",
      );

      // Refresh the comments list
      refetch();
    } catch (error: any) {
      toast.error(`Failed to send: ${error?.message || "Unknown error"}`);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendComment();
    }
  };

  const hasContent =
    newComment.trim() || mentionChips.length > 0 || selectedFiles.length > 0;

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const renderSelectedFiles = () => {
    if (selectedFiles.length === 0) return null;

    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          maxHeight: isRiskWorkspace ? 144 : 100,
          overflowY: "auto",
          p: 1,
          backgroundColor: theme.palette.grey[50],
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          "&::-webkit-scrollbar": {
            height: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.grey[300],
            borderRadius: "10px",
          },
        }}
      >
        {selectedFiles.map((file, index) => {
          const fileKey = `${file.name}-${file.size}-${file.lastModified || Date.now()}-${file.type || "unknown"}`;
          return (
            <Box
              key={fileKey}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.75,
                borderRadius: 1,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                maxWidth: "100%",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
                  borderColor: theme.palette.primary.light,
                },
              }}
            >
              <AttachFileIcon
                sx={{
                  fontSize: 16,
                  color: theme.palette.primary.main,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  maxWidth: { xs: 140, sm: 220 },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontWeight: 500,
                }}
                title={`${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`}
              >
                {file.name}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedFiles((prev) =>
                    prev.filter((_, i) => i !== index),
                  );
                }}
                sx={{
                  p: 0.25,
                  ml: "auto",
                  color: theme.palette.error.main,
                  "&:hover": {
                    backgroundColor: theme.palette.error.light,
                    color: theme.palette.error.dark,
                  },
                }}
              >
                <CancelIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          );
        })}
      </Box>
    );
  };

  if (!open) return null;

  // Calculate right position based on drawer state - ensures modal never gets hidden
  const calculateRight = () => {
    // On mobile, always use responsive positioning
    if (!matchesMd) {
      return "0px";
    }

    // On desktop (md+), adjust based on drawer state
    if (profileDrawerOpen) {
      // Drawer is open - position modal to the left of drawer with proper spacing
      const gutter = theme.spacing(3);
      return `calc(${drawerWidth}px + ${gutter})`;
    }
    // Drawer is closed - normal positioning
    return theme.spacing(2.5);
  };

  /** Stop clicks inside the modal from bubbling in the React tree so table row click (e.g. maps list) does not fire when opening from a portaled cell */
  const stopModalClickBubble = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
  };

  return createPortal(
    <Box
      onClick={stopModalClickBubble}
      onMouseDown={stopModalClickBubble}
      onPointerDown={stopModalClickBubble}
      sx={{
        position: "fixed",
        bottom: isRiskWorkspace ? "auto" : { xs: 0, sm: theme.spacing(2.5) },
        top: isRiskWorkspace
          ? { xs: 0, sm: theme.spacing(12), lg: theme.spacing(18) }
          : "auto",
        left: isRiskWorkspace
          ? {
              xs: 0,
              sm: theme.spacing(3),
              lg: `calc(320px + ${theme.spacing(2)})`,
            }
          : "auto",
        right: isRiskWorkspace
          ? {
              xs: 0,
              sm: theme.spacing(3),
              lg: `calc(88px + ${theme.spacing(3)})`,
            }
          : calculateRight(),
        transform: "none",
        width: {
          xs: "100dvw",
          sm: isRiskWorkspace
            ? "auto"
            : "min(88dvw, clamp(18rem, 38vw, 28rem))",
          md: isRiskWorkspace
            ? "auto"
            : profileDrawerOpen
              ? `min(calc(100dvw - ${drawerWidth}px - ${theme.spacing(6)}), clamp(20rem, 30vw, 28rem))`
              : `min(calc(100dvw - ${theme.spacing(6)}), clamp(20rem, 30vw, 28rem))`,
        },
        maxWidth: isRiskWorkspace
          ? { xs: "100dvw", sm: "none" }
          : { xs: "100dvw", sm: "88dvw", md: "min(28rem, 36dvw)" },
        minWidth: isRiskWorkspace
          ? { xs: "100dvw", sm: 0 }
          : {
              xs: "100dvw",
              sm: "min(88dvw, 18rem)",
              md: `min(20rem, calc(100dvw - ${theme.spacing(6)}))`,
            },
        height: {
          xs: "100dvh",
          sm: isRiskWorkspace
            ? "auto"
            : `min(calc(100dvh - ${theme.spacing(6)}), clamp(22rem, 60dvh, 36rem))`,
          md: isRiskWorkspace
            ? "auto"
            : `min(calc(100dvh - ${theme.spacing(6)}), clamp(24rem, 60dvh, 38rem))`,
        },
        ...(isRiskWorkspace && {
          bottom: { xs: 0, sm: theme.spacing(3) },
        }),
        maxHeight: `calc(100dvh - ${theme.spacing(1)})`,
        backgroundColor: theme.palette.background.paper,
        borderRadius: { xs: 0, sm: 2.5 },
        boxShadow:
          "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
        display: "flex",
        flexDirection: "column",
        zIndex: 1300,
        border: { xs: "none", sm: `1px solid ${theme.palette.divider}` },
        overflow: "hidden",
        overscrollBehavior: "contain",
        transition:
          "right 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        "@supports not (height: 100dvh)": {
          height: {
            xs: "100vh",
            sm: isRiskWorkspace
              ? "auto"
              : `min(calc(100vh - ${theme.spacing(6)}), clamp(22rem, 60vh, 36rem))`,
            md: isRiskWorkspace
              ? "auto"
              : `min(calc(100vh - ${theme.spacing(6)}), clamp(24rem, 60vh, 38rem))`,
          },
          maxHeight: `calc(100vh - ${theme.spacing(1)})`,
        },
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={{ xs: 2, sm: 2.5 }}
        py={1.5}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: theme.palette.primary.contrastText,
          borderTopLeftRadius: { xs: 0, sm: 2.5 },
          borderTopRightRadius: { xs: 0, sm: 2.5 },
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          minHeight: theme.spacing(8),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: theme.spacing(5),
              height: theme.spacing(5),
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(10px)",
              flexShrink: 0,
            }}
          >
            <ChatIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                fontSize: { xs: "1rem", sm: "1.125rem" },
                lineHeight: 1.3,
                letterSpacing: "-0.01em",
              }}
            >
              {title}
            </Typography>
            {/* <Typography 
              variant="caption" 
              sx={{ 
                opacity: 0.9,
                display: "block",
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
              }}
              title={leadName}
            >
              {leadName}
            </Typography> */}
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "inherit",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            width: 36,
            height: 36,
            flexShrink: 0,
            ml: 1,
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              transform: "rotate(90deg)",
            },
            transition: "all 0.3s ease",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Messages Area */}
      <Box
        flex={1}
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          p: { xs: 1, sm: 1.5 },
          background: isRiskWorkspace
            ? theme.palette.grey[50]
            : "linear-gradient(to bottom, #F8F9FA 0%, #FFFFFF 100%)",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overscrollBehaviorY: "contain",
          WebkitOverflowScrolling: "touch",
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.grey[300],
            borderRadius: "10px",
            "&:hover": {
              background: theme.palette.grey[400],
            },
          },
        }}
      >
        {isRiskWorkspace ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 18rem" },
              gap: 2,
              alignItems: "start",
            }}
          >
            <Stack spacing={2}>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 1,
                  bgcolor: theme.palette.background.paper,
                  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
                }}
              >
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={800}>
                      Add Internal Risk Note
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Capture risk observations and supporting documents for the
                      internal team.
                    </Typography>
                  </Box>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg"
                    onChange={handleFileSelection}
                  />

                  <Box
                    ref={inputAnchorRef}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      bgcolor: theme.palette.grey[50],
                      px: 1.5,
                      py: 1,
                      "&:focus-within": {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
                        bgcolor: theme.palette.background.paper,
                      },
                    }}
                  >
                    <InputBase
                      fullWidth
                      multiline
                      minRows={5}
                      maxRows={8}
                      placeholder="Write the risk note here..."
                      value={newComment}
                      onChange={(e) => {
                        setNewComment(e.target.value);
                        setMentionDropdownClosedUntilChange(false);
                      }}
                      sx={{
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                        alignItems: "flex-start",
                      }}
                      inputProps={{ "aria-label": "Internal risk note" }}
                    />
                  </Box>

                  {renderSelectedFiles()}

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    spacing={1}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      Add documents
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={
                        isCreating || isUploading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <SendIcon />
                        )
                      }
                      onClick={handleSendComment}
                      disabled={
                        isLoading || isCreating || isUploading || !hasContent
                      }
                    >
                      Save note
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              <Stack spacing={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="subtitle1" fontWeight={800}>
                    Notes History
                  </Typography>
                  <Chip size="small" label={sortedComments.length} />
                </Stack>
                {isLoading || isCreating ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress size={32} />
                  </Box>
                ) : sortedComments.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: 1,
                      textAlign: "center",
                      color: "text.secondary",
                      bgcolor: theme.palette.background.paper,
                    }}
                  >
                    <Typography variant="body1" fontWeight={700}>
                      No risk notes yet
                    </Typography>
                    <Typography variant="body2">
                      Saved notes will appear here with their documents.
                    </Typography>
                  </Paper>
                ) : (
                  sortedComments.map((comment) => (
                    <CommentItem
                      key={comment.id}
                      comment={comment}
                      currentUser={currentUser}
                      presentation={presentation}
                    />
                  ))
                )}
                <div ref={messagesEndRef} />
              </Stack>
            </Stack>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 1,
                position: { md: "sticky" },
                top: 0,
                bgcolor: theme.palette.background.paper,
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2" fontWeight={700}>
                    Attachments
                  </Typography>
                  <Chip size="small" label={allAttachments.length} />
                </Stack>
                <Divider />
                {allAttachments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No attachments added yet.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {allAttachments.map((att) => (
                      <Box
                        key={`${att.commentId}-${att.id}`}
                        component="a"
                        href={att.url || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: "block",
                          p: 1,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          color: "text.primary",
                          textDecoration: "none",
                          bgcolor: theme.palette.action.hover,
                          cursor: att.url ? "pointer" : "default",
                          "&:hover": att.url
                            ? {
                                borderColor: theme.palette.primary.main,
                                bgcolor: theme.palette.action.selected,
                              }
                            : {},
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <AttachFileIcon
                            sx={{
                              fontSize: 18,
                              color: theme.palette.primary.main,
                              flexShrink: 0,
                            }}
                          />
                          <Box minWidth={0}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              noWrap
                              title={att.name}
                            >
                              {att.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              display="block"
                            >
                              {att.contactName} •{" "}
                              {moment(att.sendAt).format("DD MMM")}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Box>
        ) : isLoading || isCreating ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={32} />
          </Box>
        ) : comments.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={4}
            color="text.secondary"
            sx={{ minHeight: 200 }}
          >
            <ChatIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              No comments yet
            </Typography>
            <Typography
              variant="body2"
              textAlign="center"
              sx={{ opacity: 0.7 }}
            >
              Start the conversation
            </Typography>
          </Box>
        ) : (
          <>
            {sortedComments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                currentUser={currentUser}
                presentation={presentation}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Input Area */}
      {!isRiskWorkspace && (
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            borderRadius: { xs: 0, sm: "0 0 10px 10px" },
            boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          {/* Selected files preview - Show above input */}
          {selectedFiles.length > 0 && (
            <Box
              sx={{
                mb: 1.5,
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                maxHeight: 100,
                overflowY: "auto",
                p: 1,
                backgroundColor: theme.palette.grey[50],
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                "&::-webkit-scrollbar": {
                  height: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: theme.palette.grey[300],
                  borderRadius: "10px",
                },
              }}
            >
              {selectedFiles.map((file, index) => {
                const fileKey = `${file.name}-${file.size}-${file.lastModified || Date.now()}-${file.type || "unknown"}`;
                return (
                  <Box
                    key={fileKey}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1.5,
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                      maxWidth: "100%",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
                        borderColor: theme.palette.primary.light,
                      },
                    }}
                  >
                    <AttachFileIcon
                      sx={{
                        fontSize: 16,
                        color: theme.palette.primary.main,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.7rem",
                        maxWidth: { xs: 100, sm: 140 },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontWeight: 500,
                      }}
                      title={`${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`}
                    >
                      {file.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedFiles((prev) =>
                          prev.filter((_, i) => i !== index),
                        );
                      }}
                      sx={{
                        p: 0.25,
                        ml: "auto",
                        color: theme.palette.error.main,
                        "&:hover": {
                          backgroundColor: theme.palette.error.light,
                          color: theme.palette.error.dark,
                        },
                      }}
                    >
                      <CancelIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}

          <Stack direction="row" spacing={1} alignItems="flex-end">
            {/* Hidden file input for attachments */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg"
              onChange={handleFileSelection}
            />
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              size="small"
              sx={{
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.primary.main,
                width: 38,
                height: 38,
                borderRadius: "50%",
                border: `1.5px solid ${theme.palette.divider}`,
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                  borderColor: theme.palette.primary.main,
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
              title="Attach files (PDF, DOCX, XLSX, PNG, JPG)"
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>

            <Box
              ref={inputAnchorRef}
              sx={{
                position: "relative",
                flex: 1,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 0.75,
                minHeight: 42,
                py: 1,
                px: 1.5,
                borderRadius: 2.5,
                backgroundColor: theme.palette.grey[50],
                border: "1px solid",
                borderColor: theme.palette.divider,
                "&:hover": { borderColor: theme.palette.primary.light },
                "&:focus-within": {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
                },
              }}
            >
              {enableMentions &&
                mentionChips.map((chip) => (
                  <Chip
                    key={chip.id}
                    label={
                      chip.type === "department"
                        ? `Dept: ${chip.name}`
                        : chip.name
                    }
                    size="small"
                    color="primary"
                    onDelete={() =>
                      setMentionChips((prev) =>
                        prev.filter((c) => c.id !== chip.id),
                      )
                    }
                    sx={{ height: 26, fontWeight: 500 }}
                  />
                ))}
              <InputBase
                fullWidth
                multiline
                maxRows={isRiskWorkspace ? 6 : 4}
                placeholder={
                  isRiskWorkspace
                    ? "Add an internal risk note. Attach files when needed."
                    : enableMentions
                      ? "Type a message, use @ to mention someone..."
                      : "Type a message or attach files..."
                }
                value={newComment}
                onChange={(e) => {
                  setNewComment(e.target.value);
                  setMentionDropdownClosedUntilChange(false);
                }}
                onKeyDown={handleInputKeyDown}
                sx={{
                  flex: 1,
                  minWidth: 120,
                  fontSize: "0.875rem",
                  "& .MuiInputBase-input": { py: 0.25, px: 0.5 },
                }}
                inputProps={{ "aria-label": "Comment message" }}
              />
              {showMentionPopper && (
                <Popper
                  open
                  placement="bottom-start"
                  anchorEl={inputAnchorRef.current}
                  disablePortal={false}
                  modifiers={[
                    { name: "flip", enabled: true },
                    { name: "preventOverflow", enabled: true },
                  ]}
                  sx={{ zIndex: 1500 }}
                >
                  <Paper
                    elevation={8}
                    sx={{
                      maxHeight: 220,
                      overflow: "auto",
                      minWidth: 240,
                      mt: 0.5,
                    }}
                  >
                    <List dense>
                      {isMentionSearching ? (
                        <ListItemButton disabled>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              py: 1,
                            }}
                          >
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">
                              Searching...
                            </Typography>
                          </Box>
                        </ListItemButton>
                      ) : filteredMentionContacts.length === 0 ? (
                        <ListItemButton disabled>
                          <Typography variant="body2" color="text.secondary">
                            No contacts found
                          </Typography>
                        </ListItemButton>
                      ) : (
                        filteredMentionContacts.map((c: IMentionContact) => {
                          const displayName =
                            c.name ||
                            [c.first_name, c.last_name]
                              .filter(Boolean)
                              .join(" ")
                              .trim() ||
                            "Unknown";
                          return (
                            <ListItemButton
                              key={c.id}
                              onClick={() => {
                                const lastAt = newComment.lastIndexOf("@");
                                const before = newComment.slice(0, lastAt);
                                setNewComment(before);
                                if (!mentionChips.some((m) => m.id === c.id)) {
                                  setMentionChips((prev) => [
                                    ...prev,
                                    {
                                      id: c.id,
                                      name: displayName,
                                      type: c.is_department
                                        ? "department"
                                        : "contact",
                                    },
                                  ]);
                                }
                                setMentionDropdownClosedUntilChange(true);
                              }}
                            >
                              <ListItemAvatar>
                                <Avatar
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor: "primary.main",
                                  }}
                                >
                                  <PersonIcon fontSize="small" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText primary={displayName} />
                            </ListItemButton>
                          );
                        })
                      )}
                    </List>
                  </Paper>
                </Popper>
              )}
            </Box>

            {(() => {
              const isSendDisabled =
                isLoading || isCreating || isUploading || !hasContent;

              return (
                <IconButton
                  onClick={handleSendComment}
                  disabled={isSendDisabled}
                  size="small"
                  sx={{
                    // background: isSendDisabled
                    //   ? theme.palette.grey[200]
                    //   : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    // color: isSendDisabled
                    //   ? theme.palette.grey[400]
                    //   : "white",
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    flexShrink: 0,
                    boxShadow: isSendDisabled
                      ? "none"
                      : "0 3px 10px rgba(25, 118, 210, 0.35)",
                    border: isSendDisabled
                      ? `2px solid ${theme.palette.grey[300]}`
                      : "2px solid transparent",
                    "&:hover": {
                      background: isSendDisabled
                        ? theme.palette.grey[200]
                        : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      transform: isSendDisabled ? "none" : "scale(1.1)",
                      boxShadow: isSendDisabled
                        ? "none"
                        : "0 5px 15px rgba(25, 118, 210, 0.45)",
                    },
                    "&:active": {
                      transform: isSendDisabled ? "none" : "scale(0.95)",
                    },
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {isCreating || isUploading ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    <SendIcon
                      fontSize="small"
                      sx={{
                        transition: "transform 0.2s ease",
                        transform: isSendDisabled ? "none" : "rotate(-45deg)",
                      }}
                    />
                  )}
                </IconButton>
              );
            })()}
          </Stack>
        </Box>
      )}
    </Box>,
    document.body,
  );
};

export default LeadCommentsModal;
